// Modul auth — orkestrasi login/logout/sesi. UI (login.view) memanggil ini;
// akses Supabase lewat sini (bukan langsung dari view).
import { supabase } from '@core/supabase';
import type { Employee, Session } from '@core/types';

export type { Session };

/**
 * Login via Supabase Auth (email/username + password).
 * Catatan F0: profil karyawan diambil dari tabel `employees` (RLS) setelah auth.
 */
/**
 * Karyawan cukup mengetik USERNAME (`teknisi`), bukan `teknisi@ifics.local` — domain itu urusan
 * dalam, tak perlu dihafal orang. Yang mengandung '@' dianggap email asli (Owner) dan dibiarkan
 * apa adanya. (Domainnya tak bisa dipersingkat jadi '@ifics': alamat tanpa titik ditolak Supabase.)
 */
export const DOMAIN_INTERNAL = '@ifics.local';
export const keEmailLogin = (masuk: string): string => {
  const v = (masuk ?? '').trim().toLowerCase();
  return v.includes('@') ? v : (v ? v + DOMAIN_INTERNAL : v);
};

/**
 * Pesan gagal login dalam bahasa Indonesia. Supabase menjawab "Invalid login credentials" untuk
 * SEMUA kegagalan — username salah, sandi salah, atau akunnya memang tak ada — dan itu tak boleh
 * dirinci: menyebut "akun tidak ditemukan" memberi tahu penebak mana username yang benar-benar ada.
 *
 * Yang boleh dibantu adalah kasus yang menjebak orangnya sendiri: akun yang memakai email asli
 * (mis. Gmail) TIDAK bisa masuk dengan mengetik username saja, karena input tanpa '@' otomatis
 * diberi domain internal. Petunjuk ini tak membocorkan apa pun — berlaku sama untuk siapa saja.
 */
export function pesanLoginGagal(pesanAsli: string | undefined, idLogin: string): string {
  const salahKredensial = /invalid login credentials/i.test(pesanAsli ?? '');
  if (!salahKredensial) return pesanAsli ?? 'Login gagal.';
  const pakaiUsername = !(idLogin ?? '').includes('@');
  return 'Username/email atau kata sandi salah.'
    + (pakaiUsername ? ' Kalau akun Anda memakai alamat email (mis. Gmail), ketik email lengkapnya.' : '');
}

export async function login(idLogin: string, password: string): Promise<Session> {
  const sb = supabase();
  const email = keEmailLogin(idLogin);
  const { data, error } = await sb.auth.signInWithPassword({ email, password });
  if (error || !data.user) throw new Error(pesanLoginGagal(error?.message, idLogin));

  const { data: emp, error: e2 } = await sb
    .from('employees')
    .select('id, nama, role, outlet, scope, aktif')
    .eq('auth_id', data.user.id)
    .single();
  if (e2 || !emp) throw new Error('Profil karyawan tidak ditemukan / non-aktif');
  if (emp.aktif === false) throw new Error('Akun di-suspend');

  return { user: emp as Employee };
}

/**
 * Buktikan password milik user yang SEDANG login. Supabase tak punya "verifyPassword", jadi caranya
 * login ulang dengan email akun ini: gagal = password salah, dan sesi yang berjalan tetap utuh.
 * Dipakai sebagai konfirmasi sebelum aksi sensitif (ganti password, hapus transaksi).
 */
export async function verifyOwnPassword(password: string, pesanGagal = 'Password salah.'): Promise<void> {
  const pw = (password ?? '').trim();
  if (!pw) throw new Error('Password wajib diisi.');
  const sb = supabase();
  const { data: sesi } = await sb.auth.getUser();
  const email = sesi?.user?.email;
  if (!email) throw new Error('Sesi tidak valid — silakan login ulang.');
  const { error } = await sb.auth.signInWithPassword({ email, password: pw });
  if (error) throw new Error(pesanGagal);
}

export async function logout(): Promise<void> {
  await supabase().auth.signOut();
}

/**
 * Ganti password SENDIRI (user yang sedang login). Tak butuh service-role — Supabase Auth
 * mengizinkan pengguna terautentikasi mengubah password-nya via updateUser. Wajib untuk deploy:
 * password sementara dari admin harus bisa diganti pemiliknya. Reset password ORANG LAIN tetap
 * lewat Edge Function (Owner/Manager, karyawan.service.provisionAkun).
 */
export async function changeOwnPassword(oldPassword: string, newPassword: string): Promise<void> {
  const lama = (oldPassword ?? '').trim();
  const p = (newPassword ?? '').trim();
  if (!lama) throw new Error('Password lama wajib diisi.');
  if (p.length < 6) throw new Error('Password baru minimal 6 karakter.');
  if (p === lama) throw new Error('Password baru harus berbeda dari yang lama.');
  const sb = supabase();
  // Password LAMA wajib dibuktikan dulu (Apo 28 Jul). Tanpa ini, siapa pun yang menemukan
  // perangkat dalam keadaan masih login bisa mengambil alih akun dengan mengganti passwordnya —
  // pemilik sah langsung terkunci. Supabase tak punya "verifyPassword", jadi caranya login ulang
  // memakai email akun ini + password lama: gagal = password salah, sesi lama tetap utuh.
  await verifyOwnPassword(lama, 'Password lama salah.');
  const { error } = await sb.auth.updateUser({ password: p });
  if (error) throw new Error(error.message);
}

/**
 * Ganti EMAIL LOGIN sendiri. Password diminta ulang lebih dulu — email login adalah kunci jalur
 * "lupa password", jadi kalau ia bisa diganti begitu saja di perangkat yang ditinggal terbuka,
 * seluruh pengaman lain jadi percuma. Supabase mengirim tautan konfirmasi ke alamat LAMA dan BARU;
 * emailnya baru benar-benar berpindah setelah tautan itu diklik.
 */
export async function changeOwnEmail(emailBaru: string, password: string): Promise<void> {
  const baru = (emailBaru ?? '').trim().toLowerCase();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(baru)) throw new Error('Format email tidak valid (mis. nama@email.com).');
  const sb = supabase();
  const { data } = await sb.auth.getUser();
  if (data?.user?.email?.toLowerCase() === baru) throw new Error('Email itu sudah dipakai akun ini.');
  await verifyOwnPassword(password);
  const { error } = await sb.auth.updateUser({ email: baru });
  if (error) throw new Error(error.message);
}

export async function currentSession(): Promise<Session | null> {
  const sb = supabase();
  const { data } = await sb.auth.getSession();
  if (!data.session) return null;
  const { data: emp } = await sb
    .from('employees')
    .select('id, nama, role, outlet, scope, aktif')
    .eq('auth_id', data.session.user.id)
    .single();
  // Karyawan yang di-suspend TIDAK boleh melanjutkan sesi: tanpa cek ini, cukup refresh halaman
  // untuk masuk penuh walau login awalnya sudah ditolak (token terlanjur tersimpan).
  if (!emp || (emp as Employee & { aktif?: boolean }).aktif === false) { await sb.auth.signOut(); return null; }
  return { user: emp as Employee };
}
